import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { FolderPlus } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import SearchableCombobox from '../ui/SearchableCombobox';

const CreateFolderModal = ({ isOpen, onClose, onFolderCreated, parentId = null, scope = 'personal', defaultDepartmentId = null }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(defaultDepartmentId || '');

  useEffect(() => {
    if (!isOpen) return;
    setDepartmentId(defaultDepartmentId || '');
  }, [isOpen, defaultDepartmentId]);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!isOpen) return;
      if (String(scope || 'personal').toLowerCase() !== 'workspace') return;
      if (defaultDepartmentId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/hierarchy/departments', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.success) setDepartments(response.data.data || []);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, [isOpen, scope, defaultDepartmentId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Folder name is required');
      return;
    }

    const normalizedScope = String(scope || 'personal').toLowerCase() === 'workspace' ? 'workspace' : 'personal';
    const deptToSend = normalizedScope === 'workspace' ? (defaultDepartmentId || departmentId) : null;
    if (normalizedScope === 'workspace' && !deptToSend) {
      toast.error('Department is required for workspace folder');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/efile/folder',
        { 
          name, 
          parentId,
          scope: normalizedScope,
          departmentId: deptToSend || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Folder created successfully');
        setName('');
        onFolderCreated(response.data.data);
        onClose();
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error(error.response?.data?.message || 'Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Folder">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Folder Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter folder name..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full"
            required
            autoFocus
          />
        </div>

        {String(scope || 'personal').toLowerCase() === 'workspace' && !defaultDepartmentId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department <span className="text-red-500">*</span>
            </label>
            <SearchableCombobox
              items={(departments || []).map(d => ({ label: d.name, value: d.id }))}
              value={departmentId}
              onChange={(val) => setDepartmentId(val)}
              placeholder="Select Department"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex items-center gap-2">
            <FolderPlus className="h-4 w-4" />
            {loading ? 'Creating...' : 'Create Folder'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateFolderModal;
